/**
 * Environment Configuration
 * 
 * Provides environment-specific configuration for both web and React Native.
 * 
 * Usage:
 *   import { config } from '../config/environment';
 *   
 *   const apiUrl = config.apiBaseURL;
 */

import { Platform } from '../utils/platform';

// Get environment from process.env or defaults
const getEnv = () => {
  // Check if we're in React Native without requiring the module
  const isRN = Platform.isReactNative;
  
  if (isRN) {
    // React Native: Use environment variables from .env files
    return {
      NODE_ENV: process.env.NODE_ENV || 'development',
      API_URL: process.env.REACT_APP_API_URL || process.env.EXPO_PUBLIC_API_URL,
      ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT || process.env.EXPO_PUBLIC_ENVIRONMENT || 'development'
    };
  } else {
    // Web: Use process.env
    return {
      NODE_ENV: process.env.NODE_ENV || 'development',
      API_URL: process.env.REACT_APP_API_URL,
      ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT || 'development'
    };
  }
};

const env = getEnv();

// Determine base URL
let baseURL;

if (env.API_URL) {
  // Explicit API URL from environment
  baseURL = env.API_URL;
} else if (Platform.isWeb && typeof window !== 'undefined' && process.env.REACT_APP_DOCKER === 'true') {
  // Docker mode: Use relative URLs so the proxy handles API requests
  // The setupProxy.js will route /api requests to django:8000
  baseURL = '';
} else if (Platform.isWeb && typeof window !== 'undefined') {
  // Web: Detect from hostname
  const hostname = window.location.hostname;
  
  if (hostname === 'www.tuneswell.com' || hostname === 'tuneswell.com') {
    // Use the current origin to avoid CORS issues
    // If user is on tuneswell.com, use tuneswell.com; if on www.tuneswell.com, use www.tuneswell.com
    baseURL = window.location.origin;
  } else if (hostname === 'staging.tuneswell.com') {
    baseURL = 'https://staging.tuneswell.com';
  } else if (hostname === 'localhost') {
    // For localhost, detect backend port based on frontend port
    // Staging runs on 3001 -> backend on 8001
    // Development runs on 3000 -> backend on 8000
    const frontendPort = window.location.port;
    if (frontendPort === '3001') {
      baseURL = 'http://localhost:8001';
    } else {
      baseURL = 'http://localhost:8000';
    }
  } else {
    baseURL = 'http://localhost:8000';
  }
} else if (Platform.isReactNative) {
  // React Native: Use development default or environment variable
  // Note: This code path won't execute in web builds, but is here for React Native
  if (env.NODE_ENV === 'production') {
    baseURL = 'https://www.tuneswell.com';
  } else if (env.ENVIRONMENT === 'staging') {
    baseURL = 'https://staging.tuneswell.com';
  } else {
    // Development: For physical devices, use computer's IP address
    // For simulator, localhost works fine
    // Check if we can detect simulator vs physical device
    // __DEV__ is a React Native global, check if it exists
    // eslint-disable-next-line no-undef
    const devMode = typeof __DEV__ !== 'undefined' ? __DEV__ : process.env.NODE_ENV !== 'production';
    const isSimulator = Platform.OS === 'ios' && devMode;
    
    // Try to get IP from environment variable first
    if (env.API_URL) {
      baseURL = env.API_URL;
    } else if (isSimulator) {
      // iOS Simulator can use localhost
      baseURL = 'http://localhost:8000';
    } else {
      // Physical device: Try to use common development IP
      // User can override with EXPO_PUBLIC_API_URL environment variable
      // Default to localhost, but this won't work on physical device
      // User should set EXPO_PUBLIC_API_URL=http://192.168.11.225:8000
      baseURL = 'http://localhost:8000';
    }
  }
} else {
  // Fallback
  baseURL = 'http://localhost:8000';
}

// API base URL
const apiBaseURL = `${baseURL}/api`;

/**
 * Configuration object
 */
export const config = {
  // Environment
  environment: env.ENVIRONMENT,
  isDevelopment: env.NODE_ENV === 'development',
  isProduction: env.NODE_ENV === 'production',
  isStaging: env.ENVIRONMENT === 'staging',
  
  // URLs
  baseURL,
  apiBaseURL,
  
  // Platform
  platform: Platform.OS,
  isWeb: Platform.isWeb,
  isMobile: Platform.isMobile,
  
  // Feature flags (can be environment-specific)
  features: {
    // Add feature flags here
    enablePushNotifications: !Platform.isWeb,
    enableOfflineMode: Platform.isMobile,
    enableBiometricAuth: Platform.isMobile,
  }
};

export default config;

